import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useCooldownCarousel } from "../useCooldownCarousel";
import { testimonials, translations, type Lang } from "../content";

export default function Reviews({ lang }: { lang: Lang }) {
  const t = translations[lang].reviews;
  const dir = lang === "he" ? "rtl" : "ltr";

  const { emblaRef, scrollPrev, scrollNext } = useCooldownCarousel({
    align: "start",
    loop: true,
    direction: dir,
  });

  return (
    <section id="reviews" className="py-10 md:py-16 overflow-hidden">
      <div className="container mx-auto px-6 md:px-10" dir={dir}>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10 md:mb-12">
          <div>
            <h2 className="display-lg text-3xl md:text-4xl lg:text-5xl text-foreground">
              {t.heading}
            </h2>
            <p className="text-lg text-muted-foreground mt-3 font-light">{t.subheading}</p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={scrollPrev}
              aria-label="Previous"
              className="p-3 rounded-full border border-border/15 text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors"
            >
              <ChevronRight className="w-4 h-4 ltr:hidden" />
              <ChevronLeft className="w-4 h-4 rtl:hidden" />
            </button>
            <button
              onClick={scrollNext}
              aria-label="Next"
              className="p-3 rounded-full border border-border/15 text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors"
            >
              <ChevronLeft className="w-4 h-4 ltr:hidden" />
              <ChevronRight className="w-4 h-4 rtl:hidden" />
            </button>
          </div>
        </div>

        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex ltr:-ml-6 rtl:-mr-6 md:ltr:-ml-10 md:rtl:-mr-10">
            {testimonials.map((review) => (
              <div
                key={review.id}
                className="ltr:pl-6 rtl:pr-6 md:ltr:pl-10 md:rtl:pr-10 min-w-0
                           flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%]"
              >
                <div className="h-full flex flex-col ltr:border-l rtl:border-r border-border/10 ltr:pl-6 rtl:pr-6">
                  <div className="flex gap-1 mb-6">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < review.rating ? "text-primary fill-primary" : "text-muted-foreground"
                        }`}
                      />
                    ))}
                  </div>
                  <blockquote className="flex-grow">
                    <p className="text-base md:text-lg text-foreground/85 leading-relaxed font-light whitespace-pre-line">
                      "{review.content}"
                    </p>
                  </blockquote>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
